import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Download, ArchiveRestore, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

const KIND_ORDER = ["preference", "expertise", "tooling", "relationship", "fact", "style", "other"] as const;
const KIND_LABEL: Record<string, string> = {
  preference: "Preferences",
  expertise: "Expertise",
  tooling: "Tooling",
  relationship: "Relationships",
  fact: "Facts",
  style: "Style",
  other: "Other",
};
const ADD_KINDS = ["preference", "expertise", "tooling", "relationship", "other"];
const MAX_CHARS = 1000;

function fmt(d?: string | null) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
}

type Row = {
  id: string;
  user_id: string;
  memory_text: string;
  memory_kind: string;
  is_active: boolean;
  use_count: number | null;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
  source_chat_id: string | null;
};

export default function ResearchChatMemory() {
  const qc = useQueryClient();
  const { enabled: flagOn, isLoading: flagLoading } = useFeatureFlag("phase_3_memory_settings");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<Row | null>(null);
  const [editText, setEditText] = useState("");
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");
  const [newKind, setNewKind] = useState("preference");
  const [forgetTarget, setForgetTarget] = useState<Row | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["research_user_memory"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("research_user_memory")
        .select("*")
        .order("updated_at", { ascending: false })
        .limit(1000);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const grouped = useMemo(() => {
    const rows = (data ?? []).filter((r) => (showArchived ? !r.is_active : r.is_active));
    const map = new Map<string, Row[]>();
    for (const r of rows) {
      const k = KIND_ORDER.includes(r.memory_kind as any) ? r.memory_kind : "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(r);
    }
    return KIND_ORDER.map((k) => ({ kind: k, rows: map.get(k) ?? [] }));
  }, [data, showArchived]);

  async function auditLog(action: string, entity_id?: string, metadata: any = {}) {
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return;
    await supabase.from("research_audit_log").insert({
      user_id: u.user.id,
      action,
      entity_type: "research_user_memory",
      entity_id: entity_id ?? null,
      metadata,
    });
  }

  async function saveEdit() {
    if (!editing || !editText.trim()) return;
    const value = editText.trim().slice(0, MAX_CHARS);
    const { error } = await supabase
      .from("research_user_memory")
      .update({ memory_text: value })
      .eq("id", editing.id);
    if (error) return toast.error(error.message);
    await auditLog("memory.update", editing.id, { previous_length: editing.memory_text.length });
    toast.success("Memory updated");
    setEditing(null);
    qc.invalidateQueries({ queryKey: ["research_user_memory"] });
  }

  async function confirmForget() {
    if (!forgetTarget) return;
    const { error } = await supabase
      .from("research_user_memory")
      .update({ is_active: false })
      .eq("id", forgetTarget.id);
    if (error) return toast.error(error.message);
    await auditLog("memory.forget", forgetTarget.id, { kind: forgetTarget.memory_kind });
    toast.success("Forgotten");
    setForgetTarget(null);
    qc.invalidateQueries({ queryKey: ["research_user_memory"] });
  }

  async function restore(r: Row) {
    const { error } = await supabase
      .from("research_user_memory")
      .update({ is_active: true })
      .eq("id", r.id);
    if (error) return toast.error(error.message);
    await auditLog("memory.restore", r.id);
    qc.invalidateQueries({ queryKey: ["research_user_memory"] });
  }

  async function addMemory() {
    if (!newText.trim()) return;
    const { data: u } = await supabase.auth.getUser();
    if (!u.user) return toast.error("Not signed in");
    const value = newText.trim().slice(0, MAX_CHARS);
    const { data: ins, error } = await supabase
      .from("research_user_memory")
      .insert({ memory_text: value, memory_kind: newKind, user_id: u.user.id })
      .select("id")
      .single();
    if (error) return toast.error(error.message);
    await auditLog("memory.create", ins?.id, { kind: newKind });
    toast.success("Memory added");
    setAdding(false);
    setNewText("");
    setNewKind("preference");
    qc.invalidateQueries({ queryKey: ["research_user_memory"] });
  }

  function exportJSON() {
    const blob = new Blob([JSON.stringify(data ?? [], null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const today = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `memory-export-${today}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (flagLoading) {
    return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</div>;
  }
  if (!flagOn) {
    return (
      <div className="p-8 max-w-xl mx-auto text-center">
        <h2 className="text-lg font-semibold mb-2">Memory & Preferences</h2>
        <p className="text-sm text-muted-foreground">
          This area is gated by the <code className="px-1 bg-muted rounded">phase_3_memory_settings</code> feature flag and is currently disabled.
          Ask a superadmin to enable it.
        </p>
      </div>
    );
  }

  const totalActive = (data ?? []).filter((r) => r.is_active).length;

  return (
    <div className="p-4 md:p-6 space-y-6 h-full overflow-y-auto max-w-4xl mx-auto">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Memory & Preferences</h1>
        <p className="text-sm text-muted-foreground">
          What Claude remembers about you across sessions. Edit or delete any row you want forgotten.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <Dialog open={adding} onOpenChange={setAdding}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-3.5 w-3.5 mr-1" /> Add memory</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add memory</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Select value={newKind} onValueChange={setNewKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ADD_KINDS.map((k) => (
                    <SelectItem key={k} value={k}>{KIND_LABEL[k]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value.slice(0, MAX_CHARS))}
                rows={5}
                placeholder="e.g. Prefers British English and dd/mm/yyyy dates."
              />
              <div className="text-xs text-muted-foreground text-right">{newText.length}/{MAX_CHARS}</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button onClick={addMemory} disabled={!newText.trim()}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Button size="sm" variant="outline" onClick={exportJSON}>
          <Download className="h-3.5 w-3.5 mr-1" /> Export
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setShowArchived((s) => !s)}>
          {showArchived ? "Show active" : "Show forgotten"}
        </Button>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground">{totalActive} active</span>
      </div>

      {isLoading && (
        <div className="text-center text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…
        </div>
      )}

      {!isLoading && grouped.every((g) => g.rows.length === 0) && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          {showArchived ? "Nothing forgotten yet." : "No memories saved yet. Add one to personalise future answers."}
        </CardContent></Card>
      )}

      {!isLoading && grouped.map((g) => (
        g.rows.length === 0 ? null : (
          <section key={g.kind} className="space-y-2">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {KIND_LABEL[g.kind]} <span className="text-xs font-normal">({g.rows.length})</span>
            </h2>
            <div className="space-y-2">
              {g.rows.map((r) => (
                <Card key={r.id}>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <pre className="text-sm whitespace-pre-wrap break-words font-mono">{r.memory_text}</pre>
                      </div>
                      <Badge variant="outline" className="shrink-0">{r.memory_kind}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>
                        {r.source_chat_id ? "from chat" : "manual"} · updated {fmt(r.updated_at)} · used {r.use_count ?? 0}×
                      </span>
                      <div className="space-x-1">
                        {r.is_active ? (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setEditText(r.memory_text); }}>
                              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setForgetTarget(r)}>
                              <Trash2 className="h-3.5 w-3.5 mr-1" /> Forget
                            </Button>
                          </>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => restore(r)}>
                            <ArchiveRestore className="h-3.5 w-3.5 mr-1" /> Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )
      ))}

      {/* Empty-kind hints (only when active view and at least something exists) */}
      {!isLoading && !showArchived && grouped.some((g) => g.rows.length > 0) && (
        <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t">
          {KIND_ORDER.filter((k) => k !== "other" && grouped.find((g) => g.kind === k)?.rows.length === 0).map((k) => (
            <div key={k}>Nothing remembered yet under {KIND_LABEL[k]}.</div>
          ))}
        </div>
      )}

      {/* Edit modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit memory</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Textarea
              value={editText}
              onChange={(e) => setEditText(e.target.value.slice(0, MAX_CHARS))}
              rows={6}
            />
            <div className="text-xs text-muted-foreground text-right">{editText.length}/{MAX_CHARS}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={!editText.trim()}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forget confirm */}
      <AlertDialog open={!!forgetTarget} onOpenChange={(o) => !o && setForgetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Forget this memory?</AlertDialogTitle>
            <AlertDialogDescription>
              It will no longer influence future answers. You can restore it from "Show forgotten" if you change your mind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmForget}>Forget</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
